import React from 'react';
import {
  MessageCircle,
  Mail,
  MapPin,
  Instagram,
  Phone,
  Clock,
  Calendar,
  ArrowUpRight,
} from 'lucide-react';
import { profile } from '../mock';

const highlights = [
  {
    icon: Calendar,
    title: 'Primer contacto',
    body: 'Coordinamos día, horario y modalidad por WhatsApp. Te respondo personalmente.',
  },
  {
    icon: Clock,
    title: 'Sesiones de 50 min',
    body: 'Presencial en Rosario (Colonia, Uruguay) u online por videollamada.',
  },
  {
    icon: MessageCircle,
    title: 'Sin compromiso',
    body: 'Podés escribirme tu duda primero y vemos juntas si te puedo ayudar.',
  },
];

const Contact = () => {
  return (
    <section id="contacto" className="py-24 lg:py-32 bg-[#F4EDE2] relative overflow-hidden">
      <div
        aria-hidden
        className="absolute -top-32 -right-20 w-[460px] h-[460px] rounded-full opacity-50 blur-3xl"
        style={{
          background:
            'radial-gradient(circle at 50% 50%, #D4B5A6 0%, #F4EDE2 60%, transparent 80%)',
        }}
      />

      <div className="max-w-[1300px] mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-12 relative">
        {/* Left info */}
        <div className="lg:col-span-6">
          <div className="flex items-center gap-3 mb-5">
            <span className="divider-mark" />
            <span className="eyebrow">Agendá tu consulta</span>
          </div>
          <h2 className="font-serif text-[40px] lg:text-[58px] leading-[1.02] text-[#2E2A23] tracking-[-0.02em]">
            Empecemos a
            <span className="italic text-[#B8806E]"> conversar.</span>
          </h2>
          <p className="mt-6 text-[16.5px] leading-[1.8] text-[#2E2A23]/80 font-light max-w-[520px]">
            Si tenés alguna duda respecto a las consultas de psicología o
            lactancia, escribime por WhatsApp. Te respondo personalmente, en
            cuanto pueda.
          </p>

          <div className="mt-10 flex flex-wrap items-center gap-4">
            <a
              href={profile.whatsapp}
              target="_blank"
              rel="noreferrer"
              className="btn-primary"
            >
              <MessageCircle size={16} strokeWidth={1.6} />
              Escribir por WhatsApp
            </a>
            <a href={`mailto:${profile.email}`} className="btn-ghost">
              <Mail size={16} strokeWidth={1.6} />
              Enviar un email
            </a>
          </div>

          <div className="mt-12 grid sm:grid-cols-3 gap-5">
            {highlights.map((h) => (
              <div
                key={h.title}
                className="bg-[#FAF4E8] border border-[#DBCEB4] rounded-2xl p-5"
              >
                <span className="w-10 h-10 rounded-full bg-[#E8DDC8] grid place-items-center text-[#B8806E] mb-4">
                  <h.icon size={16} strokeWidth={1.6} />
                </span>
                <p className="font-serif text-[18px] text-[#2E2A23] leading-tight">
                  {h.title}
                </p>
                <p className="mt-2 text-[13px] leading-[1.6] text-[#2E2A23]/70">
                  {h.body}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Right contact card */}
        <div className="lg:col-span-6">
          <div className="relative bg-[#2E2A23] text-[#F4EDE2] rounded-[28px] p-8 lg:p-10 overflow-hidden">
            <div
              aria-hidden
              className="absolute -bottom-24 -right-16 w-[320px] h-[320px] rounded-full opacity-30 blur-3xl"
              style={{
                background:
                  'radial-gradient(circle at 50% 50%, #B8806E 0%, transparent 70%)',
              }}
            />
            <p className="font-script text-[#D4B5A6] text-[32px] leading-none">
              hola, soy Karina
            </p>
            <h3 className="mt-3 font-serif text-[30px] lg:text-[38px] leading-[1.1] tracking-[-0.01em]">
              Acompañar es un acto de presencia.
            </h3>

            <div className="mt-10 space-y-6 relative">
              <a
                href={profile.whatsapp}
                target="_blank"
                rel="noreferrer"
                className="flex items-start gap-4 group"
              >
                <span className="w-11 h-11 rounded-full border border-[#F4EDE2]/25 grid place-items-center text-[#D4B5A6] group-hover:bg-[#B8806E] group-hover:border-[#B8806E] group-hover:text-[#FAF4E8] transition-colors duration-300">
                  <MessageCircle size={18} strokeWidth={1.5} />
                </span>
                <div className="flex-1">
                  <p className="text-[11px] tracking-[0.28em] uppercase text-[#D4B5A6]/80">
                    WhatsApp · Más rápido
                  </p>
                  <p className="font-serif text-[22px] mt-1 flex items-center gap-2">
                    {profile.whatsappLabel}
                    <ArrowUpRight
                      size={18}
                      strokeWidth={1.5}
                      className="opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300"
                    />
                  </p>
                </div>
              </a>

              <a href={`mailto:${profile.email}`} className="flex items-start gap-4 group">
                <span className="w-11 h-11 rounded-full border border-[#F4EDE2]/25 grid place-items-center text-[#D4B5A6] group-hover:bg-[#B8806E] group-hover:border-[#B8806E] group-hover:text-[#FAF4E8] transition-colors duration-300">
                  <Mail size={18} strokeWidth={1.5} />
                </span>
                <div className="flex-1">
                  <p className="text-[11px] tracking-[0.28em] uppercase text-[#D4B5A6]/80">
                    Email
                  </p>
                  <p className="font-serif text-[20px] mt-1 break-all flex items-center gap-2">
                    {profile.email}
                    <ArrowUpRight
                      size={18}
                      strokeWidth={1.5}
                      className="opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300 shrink-0"
                    />
                  </p>
                </div>
              </a>

              <div className="flex items-start gap-4">
                <span className="w-11 h-11 rounded-full border border-[#F4EDE2]/25 grid place-items-center text-[#D4B5A6]">
                  <MapPin size={18} strokeWidth={1.5} />
                </span>
                <div className="flex-1">
                  <p className="text-[11px] tracking-[0.28em] uppercase text-[#D4B5A6]/80">
                    Consultorio
                  </p>
                  <p className="font-serif text-[20px] mt-1">{profile.address}</p>
                  <p className="text-[13px] text-[#F4EDE2]/65 mt-1">
                    También online por videollamada
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-10 pt-6 border-t border-[#F4EDE2]/15 flex items-center justify-between flex-wrap gap-4">
              <p className="text-[12px] tracking-[0.18em] uppercase text-[#D4B5A6]/80">
                Encontrame también en
              </p>
              <div className="flex items-center gap-3">
                <a
                  href={profile.instagram}
                  target="_blank"
                  rel="noreferrer"
                  aria-label="Instagram"
                  className="w-10 h-10 rounded-full border border-[#F4EDE2]/25 grid place-items-center hover:bg-[#F4EDE2] hover:text-[#2E2A23] transition-colors duration-300"
                >
                  <Instagram size={16} strokeWidth={1.5} />
                </a>
                <a
                  href={profile.whatsapp}
                  target="_blank"
                  rel="noreferrer"
                  aria-label="WhatsApp"
                  className="w-10 h-10 rounded-full border border-[#F4EDE2]/25 grid place-items-center hover:bg-[#F4EDE2] hover:text-[#2E2A23] transition-colors duration-300"
                >
                  <Phone size={16} strokeWidth={1.5} />
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Floating WhatsApp button */}
      <a
        href={profile.whatsapp}
        target="_blank"
        rel="noreferrer"
        aria-label="Abrir WhatsApp"
        className="fixed bottom-6 left-6 z-40 w-14 h-14 rounded-full bg-[#B8806E] text-[#FAF4E8] grid place-items-center shadow-[0_12px_30px_-10px_rgba(155,80,56,0.7)] hover:bg-[#9C6856] transition-colors duration-300"
      >
        <MessageCircle size={22} strokeWidth={1.6} />
      </a>
    </section>
  );
};

export default Contact;
